import { forwardRef } from "react";

import {
  carouselButtonVariants,
  carouselContentVariants,
  carouselIndicatorGroupVariants,
  carouselIndicatorVariants,
  carouselItemVariants,
  carouselRootContainerVariants,
  carouselRootVariants,
  type CarouselButtonVariants,
  type CarouselContentVariants,
  type CarouselIndicatorGroupVariants,
  type CarouselIndicatorVariants,
  type CarouselItemVariants,
  type CarouselRootVariants,
} from "./carousel.styles";
import { Icon } from "./icon";

// -----------------------------------------------------------------------------

export type CarouselRootRef = React.ComponentRef<"div">;

export interface CarouselRootProps
  extends React.ComponentPropsWithoutRef<"div">, CarouselRootVariants {
  "aria-label": string;
}

const CarouselRoot = forwardRef<CarouselRootRef, CarouselRootProps>(
  ({ "aria-label": ariaLabel, children, className, ...props }, ref) => {
    return (
      <div {...props} className={carouselRootVariants({ className })}>
        <section
          aria-roledescription="carousel"
          aria-label={ariaLabel}
          className={carouselRootContainerVariants()}
          ref={ref}
        >
          {children}
        </section>
      </div>
    );
  },
);

CarouselRoot.displayName = "CarouselRoot";

export { CarouselRoot };

// -----------------------------------------------------------------------------

export type CarouselContentRef = React.ComponentRef<"div">;

export interface CarouselContentProps
  extends React.ComponentPropsWithoutRef<"div">, CarouselContentVariants {}

const CarouselContent = forwardRef<CarouselContentRef, CarouselContentProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        {...props}
        className={carouselContentVariants({ className })}
        ref={ref}
      />
    );
  },
);

CarouselContent.displayName = "CarouselContent";

export { CarouselContent };

// -----------------------------------------------------------------------------

export type CarouselItemRef = React.ComponentRef<"div">;

export interface CarouselItemProps
  extends React.ComponentPropsWithoutRef<"div">, CarouselItemVariants {
  "aria-label": string;
}

const CarouselItem = forwardRef<CarouselItemRef, CarouselItemProps>(
  ({ "aria-label": ariaLabel, className, ...props }, ref) => {
    return (
      <div
        {...props}
        // Carousel slides should be announced as grouped slide items
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
        role="group"
        aria-roledescription="slide"
        aria-label={ariaLabel}
        className={carouselItemVariants({ className })}
        ref={ref}
      />
    );
  },
);

CarouselItem.displayName = "CarouselItem";

export { CarouselItem };

// -----------------------------------------------------------------------------

export type CarouselButtonPrevRef = React.ComponentRef<"button">;

export interface CarouselButtonPrevProps
  extends
    Omit<React.ComponentPropsWithoutRef<"button">, "children" | "type">,
    Omit<CarouselButtonVariants, "placement"> {}

const CarouselButtonPrev = forwardRef<
  CarouselButtonPrevRef,
  CarouselButtonPrevProps
>(({ className, disabled, ...props }, ref) => {
  return (
    <button
      {...props}
      type="button"
      disabled={disabled}
      className={carouselButtonVariants({
        className,
        disabled,
        placement: "left",
      })}
      ref={ref}
    >
      <Icon name="ms-arrow-back" />
      <span className="sr-only">Previous slide</span>
    </button>
  );
});

CarouselButtonPrev.displayName = "CarouselButtonPrev";

export { CarouselButtonPrev };

// -----------------------------------------------------------------------------

export type CarouselButtonNextRef = React.ComponentRef<"button">;

export interface CarouselButtonNextProps
  extends
    Omit<React.ComponentPropsWithoutRef<"button">, "children" | "type">,
    Omit<CarouselButtonVariants, "placement"> {}

const CarouselButtonNext = forwardRef<
  CarouselButtonNextRef,
  CarouselButtonNextProps
>(({ className, disabled, ...props }, ref) => {
  return (
    <button
      {...props}
      type="button"
      disabled={disabled}
      className={carouselButtonVariants({
        className,
        disabled,
        placement: "right",
      })}
      ref={ref}
    >
      <Icon name="ms-arrow-forward" />
      <span className="sr-only">Next slide</span>
    </button>
  );
});

CarouselButtonNext.displayName = "CarouselButtonNext";

export { CarouselButtonNext };

// -----------------------------------------------------------------------------

export type CarouselIndicatorGroupRef = React.ComponentRef<"div">;

export interface CarouselIndicatorGroupProps
  extends
    React.ComponentPropsWithoutRef<"div">,
    CarouselIndicatorGroupVariants {}

const CarouselIndicatorGroup = forwardRef<
  CarouselIndicatorGroupRef,
  CarouselIndicatorGroupProps
>(({ className, ...props }, ref) => {
  return (
    <div
      {...props}
      className={carouselIndicatorGroupVariants({ className })}
      ref={ref}
    />
  );
});

CarouselIndicatorGroup.displayName = "CarouselIndicatorGroup";

export { CarouselIndicatorGroup };

// -----------------------------------------------------------------------------

export type CarouselIndicatorRef = React.ComponentRef<"button">;

export interface CarouselIndicatorProps
  extends
    Omit<React.ComponentPropsWithoutRef<"button">, "children" | "type">,
    CarouselIndicatorVariants {
  value: number;
}

const CarouselIndicator = forwardRef<
  CarouselIndicatorRef,
  CarouselIndicatorProps
>(({ className, value, ...props }, ref) => {
  return (
    <button
      {...props}
      type="button"
      className={carouselIndicatorVariants({ className })}
      ref={ref}
    >
      <span className="sr-only">Go to slide {value}</span>
    </button>
  );
});

CarouselIndicator.displayName = "CarouselIndicator";

export { CarouselIndicator };
